import { motion, useReducedMotion } from 'motion/react';
import { applyCdn } from '../utils/mediaUrl';
import { useEffect, useState } from 'react';
import { getSettings } from '../utils/api';

export interface EditorialBlock {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  imageUrl?: string;
  videoUrl?: string;
  imagePosition?: 'left' | 'right' | 'centered-header' | 'two-column-text';
  grayscale?: boolean;
  buttonText?: string;
  buttonLink?: string;
}

interface EditorialSectionsProps {
  sections?: EditorialBlock[];
}

export function formatEditorialText(text: string | undefined): string {
  if (!text) return '';
  let formatted = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Restore basic safe br tags
  formatted = formatted.replace(/&lt;br\s*\/?&gt;/gi, '<br />');

  // Convert *text* to italic serif normal-case
  formatted = formatted.replace(/\*(.*?)\*/g, '<em class="font-serif italic normal-case">$1</em>');

  // Convert _text_ to italic serif normal-case
  formatted = formatted.replace(/_(.*?)_/g, '<em class="font-serif italic normal-case">$1</em>');

  // Convert [wine]text[/wine] to class text-brand-wine
  formatted = formatted.replace(/\[wine\](.*?)\[\/wine\]/gi, '<span class="text-brand-wine">$1</span>');

  // Convert [ink]text[/ink] to class text-ink
  formatted = formatted.replace(/\[ink\](.*?)\[\/ink\]/gi, '<span class="text-ink">$1</span>');

  // Convert [muted]text[/muted] to class text-muted
  formatted = formatted.replace(/\[muted\](.*?)\[\/muted\]/gi, '<span class="text-muted">$1</span>');

  // Convert new lines to <br />
  formatted = formatted.replace(/\n/g, '<br />');

  return formatted;
}

export default function EditorialSections({ sections }: EditorialSectionsProps) {
  const shouldReduceMotion = useReducedMotion();
  const [cdnBaseUrl, setCdnBaseUrl] = useState('');

  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await getSettings();
        setCdnBaseUrl(settings?.cdn_base_url || '');
      } catch (error) {
        console.error('Failed to load settings in EditorialSections:', error);
      }
    }
    loadSettings();
  }, []);

  if (!sections || sections.length === 0) return null;

  return (
    <div className="w-full bg-paper py-16 md:py-24 space-y-24 md:space-y-36 overflow-hidden">
      {sections.map((section, idx) => {
        const position = section.imagePosition || 'right';
        const isGrayscale = !!section.grayscale;
        const resolvedImage = section.imageUrl ? applyCdn(section.imageUrl, cdnBaseUrl) : '';
        const resolvedVideo = section.videoUrl ? applyCdn(section.videoUrl, cdnBaseUrl) : '';

        // Animation presets
        const fadeUp = {
          initial: shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '-100px' },
          transition: { duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] as const }
        };

        const imageAnim = {
          initial: shouldReduceMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.05 },
          whileInView: { opacity: 1, scale: 1 },
          viewport: { once: true },
          transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] as const }
        };

        // 1. Centered Header layout style
        if (position === 'centered-header') {
          return (
            <div key={section.id || idx} className="max-w-[1200px] mx-auto px-6 md:px-12 flex flex-col items-center justify-center text-center">
              <motion.div {...fadeUp} className="max-w-3xl space-y-4">
                {section.subtitle && (
                  <span
                    className="text-[10px] md:text-[11px] text-brand-wine font-medium tracking-[0.25em] uppercase block rich-text-content"
                    dangerouslySetInnerHTML={{ __html: formatEditorialText(section.subtitle) }}
                  />
                )}
                {section.title && (
                  <h2
                    className="text-2xl md:text-3xl font-serif text-brand-wine leading-snug rich-text-content"
                    dangerouslySetInnerHTML={{ __html: formatEditorialText(section.title) }}
                  />
                )}
                {section.description && (
                  <div
                    dangerouslySetInnerHTML={{ __html: section.description }}
                    className="text-zinc-600 text-xs md:text-sm font-light leading-relaxed max-w-prose mx-auto rich-text-content space-y-4 pt-4 border-t border-brand-wine/5"
                  />
                )}
                {section.buttonText && section.buttonLink && (
                  <a
                    href={section.buttonLink}
                    className="inline-block px-6 py-2.5 rounded-full border border-brand-wine/20 text-brand-wine hover:bg-brand-wine hover:text-white transition-all duration-300 text-[10px] font-bold uppercase tracking-widest mt-6 cursor-pointer"
                  >
                    {section.buttonText}
                  </a>
                )}
              </motion.div>
            </div>
          );
        }

        // 2. Two-Column Text Grid layout style
        if (position === 'two-column-text') {
          return (
            <div key={section.id || idx} className="max-w-[1200px] mx-auto px-6 md:px-12">
              <motion.div
                {...fadeUp}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 border-t border-brand-wine/10 pt-16"
              >
                <div className="space-y-4">
                  {section.subtitle && (
                    <span
                      className="text-[10px] md:text-[11px] text-brand-wine font-medium tracking-[0.2em] uppercase block rich-text-content"
                      dangerouslySetInnerHTML={{ __html: formatEditorialText(section.subtitle) }}
                    />
                  )}
                  {section.title && (
                    <h3
                      className="text-2xl md:text-3xl font-serif text-brand-wine leading-snug rich-text-content"
                      dangerouslySetInnerHTML={{ __html: formatEditorialText(section.title) }}
                    />
                  )}
                </div>
                <div className="space-y-6">
                  {section.description && (
                    <div
                      dangerouslySetInnerHTML={{ __html: section.description }}
                      className="text-zinc-600 text-xs md:text-sm font-light leading-relaxed rich-text-content space-y-4"
                    />
                  )}
                  {section.buttonText && section.buttonLink && (
                    <a
                      href={section.buttonLink}
                      className="inline-block px-6 py-2.5 rounded-full border border-brand-wine/20 text-brand-wine hover:bg-brand-wine hover:text-white transition-all duration-300 text-[10px] font-bold uppercase tracking-widest cursor-pointer"
                    >
                      {section.buttonText}
                    </a>
                  )}
                </div>
              </motion.div>
            </div>
          );
        }

        // 3. Asymmetric Left or Right columns layout style
        const isLeft = position === 'left';

        return (
          <div
            key={section.id || idx}
            className={`max-w-[1200px] mx-auto px-6 md:px-12 flex flex-col ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
              } gap-12 md:gap-16 lg:gap-24 items-center`}
          >
            {/* Image Column */}
            {(resolvedImage || resolvedVideo) && (
              <div className="w-full md:w-[45%] shrink-0">
                <div className="relative aspect-[3/4] overflow-hidden bg-zinc-100/50 shadow-sm border border-black/[0.03]">
                  <motion.div {...imageAnim} className="absolute inset-0 w-full h-full">
                    {resolvedVideo ? (
                      <video
                        src={resolvedVideo}
                        className={`w-full h-full object-cover ${isGrayscale ? 'filter grayscale hover:grayscale-0' : ''} transition-all duration-700`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        poster={resolvedImage}
                      />
                    ) : (
                      <img
                        src={resolvedImage}
                        alt={section.title || section.subtitle || 'Editorial Image'}
                        className={`w-full h-full object-cover ${isGrayscale ? 'filter grayscale hover:grayscale-0' : ''} transition-all duration-700`}
                        referrerPolicy="no-referrer"
                      />
                    )}
                  </motion.div>
                </div>
              </div>
            )}

            {/* Text Column */}
            <div className="w-full flex flex-col justify-center">
              <motion.div {...fadeUp} className="space-y-4 max-w-xl">
                {section.subtitle && (
                  <span
                    className="text-[10px] md:text-[11px] text-brand-wine font-medium tracking-[0.2em] uppercase block rich-text-content"
                    dangerouslySetInnerHTML={{ __html: formatEditorialText(section.subtitle) }}
                  />
                )}
                {section.title && (
                  <h3
                    className="text-2xl md:text-3xl font-serif text-brand-wine leading-snug rich-text-content"
                    dangerouslySetInnerHTML={{ __html: formatEditorialText(section.title) }}
                  />
                )}
                {section.description && (
                  <div
                    dangerouslySetInnerHTML={{ __html: section.description }}
                    className="text-zinc-600 text-xs md:text-sm font-light leading-relaxed rich-text-content space-y-4 pt-2"
                  />
                )}
                {section.buttonText && section.buttonLink && (
                  <a
                    href={section.buttonLink}
                    className="inline-block px-6 py-2.5 rounded-full border border-brand-wine/20 text-brand-wine hover:bg-brand-wine hover:text-white transition-all duration-300 text-[10px] font-bold uppercase tracking-widest mt-6 cursor-pointer"
                  >
                    {section.buttonText}
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
